import { SimpleTab, SimpleTabContainer } from '@shared/components/core/button/SimpleTab';
import DeleteIconButton from '@shared/components/core/button/DeleteIconButton';
import CoreButton from '@shared/components/core/button/CoreButton';
import HorizontalFieldContainer from '@shared/components/core/field/HorizontalFieldContainer';
import ChipButtonContainer from '@shared/components/core/layouts/ChipButtonContainer';
import SuggestedPromptField from '@shared/components/input/field/SuggestedPromptField';
import UnsavedChangesDialog from '@shared/components/loading/UnsavedChangesDialog';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ProfileSuggestedPromptsPageContent() {
    const { t } = useTranslation();
    const isPreview = useEditProfileStore((state) => state.isPreview);
    const suggestedGeneratePrompts = useEditProfileStore((state) => state.suggestedGeneratePrompts);
    const { setHasChanges, updateSuggestedGeneratePrompts, updateSuggestedRewritePrompts, saveChanges } =
        useEditProfileStore.getState();
    const hasChanges = useEditProfileStore((state) => state.hasChanges);
    const [activeTab, setActiveTab] = useState<'generate' | 'rewrite'>('generate');
    const [createButtonLabel, setCreateButtonLabel] = useState(t('common:suggestedPrompts.addGenerate'));
    const [placeholderText, setPlaceholderText] = useState(t('common:suggestedPrompts.generatePlaceholder'));
    const [prompts, setPrompts] = useState<string[]>(suggestedGeneratePrompts || []);
    const newItemIndexRef = useRef<number | null>(null);

    useEffect(() => {
        const suggestedGeneratePrompts = useEditProfileStore.getState().suggestedGeneratePrompts;
        const suggestedRewritePrompts = useEditProfileStore.getState().suggestedRewritePrompts;
        if (activeTab === 'generate') {
            setCreateButtonLabel(t('common:suggestedPrompts.addGenerate'));
            setPlaceholderText(t('common:suggestedPrompts.generatePlaceholder'));
            setPrompts(suggestedGeneratePrompts || []);
        } else {
            setCreateButtonLabel(t('common:suggestedPrompts.addRewrite'));
            setPlaceholderText(t('common:suggestedPrompts.rewritePlaceholder'));
            setPrompts(suggestedRewritePrompts || []);
        }
    }, [activeTab]);

    const activeTabIsGenerate = activeTab === 'generate';

    const setUpdates = (updated: string[]) => {
        setPrompts(updated);
        if (activeTabIsGenerate) {
            updateSuggestedGeneratePrompts(updated);
        } else {
            updateSuggestedRewritePrompts(updated);
        }
        setHasChanges(true);
    };

    const updatePrompts = (value: string, index: number) => {
        const updated = prompts.map((p, i) => (i === index ? value : p));
        setUpdates(updated);
    };

    const removePrompts = (index: number) => {
        const updated = prompts.filter((_, i) => i !== index);
        setUpdates(updated);
    };

    const addPrompts = () => {
        const updated = [...prompts, ''];
        newItemIndexRef.current = updated.length - 1;
        setUpdates(updated);
    };

    const handleSave = async () => {
        const result = await saveChanges();
    };
    return (
        <>
            <SimpleTabContainer role="tablist" aria-label="Profile Tabs">
                <SimpleTab
                    role="tab"
                    style={{ padding: '16px 32px' }}
                    aria-selected={activeTab === 'generate'}
                    onClick={() => setActiveTab('generate')}
                    isActive={activeTab === 'generate'}>
                    {t('common:suggestedPrompts.generate')}
                </SimpleTab>
                <SimpleTab
                    role="tab"
                    style={{ padding: '16px 32px' }}
                    aria-selected={activeTab === 'rewrite'}
                    onClick={() => setActiveTab('rewrite')}
                    isActive={activeTab === 'rewrite'}>
                    {t('common:suggestedPrompts.rewrite')}
                </SimpleTab>
            </SimpleTabContainer>

            {prompts.map((prompt, i) => (
                <HorizontalFieldContainer key={i}>
                    <SuggestedPromptField
                        value={prompt}
                        onChange={(value) => updatePrompts(value, i)}
                        disabled={isPreview}
                        placeholder={placeholderText}
                        autoFocus={newItemIndexRef.current === i}
                    />
                    <DeleteIconButton disabled={isPreview} onClick={() => removePrompts(i)}>
                        {t('common:suggestedPrompts.remove')}
                    </DeleteIconButton>
                </HorizontalFieldContainer>
            ))}
            <ChipButtonContainer>
                <CoreButton $color="text" $size="large" startIcon="plus" onClick={addPrompts}>
                    {createButtonLabel}
                </CoreButton>
            </ChipButtonContainer>

            {hasChanges && (
                <UnsavedChangesDialog hasChanges={hasChanges} setHasChanges={setHasChanges} onSave={handleSave} />
            )}
        </>
    );
}
